import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const HEADERS = [
  'Sponsor',
  'Orders',
  'Non-pledge donations',
  'Pledge amount',
  'Paid',
  'Pledge balance',
  'Total revenue',
  '% Pledge paid',
];

function decodeHtml(text) {
  return String(text || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function fmtMoney(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
}

function fmtMoneyOrDash(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || Math.round(n * 100) === 0) return '—';
  return fmtMoney(n);
}

function fmtPct(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function hasPledgeMetrics(row = {}) {
  return Number(row.pledge_amount || 0) > 0;
}

function isGeneralDonationSponsor(row = {}) {
  return !hasPledgeMetrics(row) && Number(row.non_pledge_donations || 0) > 0;
}

function sponsorPledgeBalance(row = {}) {
  if (row.pledge_balance != null && row.pledge_balance !== '') {
    return Number(row.pledge_balance) || 0;
  }
  return Number(row.pledge_amount || 0) - Number(row.paid_amount || 0);
}

function sponsorDisplayName(row) {
  const name = decodeHtml(row.sponsor_name) || 'Guest';
  return isGeneralDonationSponsor(row) ? `* ${name}` : name;
}

function mapSponsorRow(row) {
  const pledge = hasPledgeMetrics(row);
  return [
    sponsorDisplayName(row),
    Number(row.orders_count || 0),
    fmtMoneyOrDash(row.non_pledge_donations),
    pledge ? fmtMoney(row.pledge_amount) : '—',
    pledge ? fmtMoney(row.paid_amount) : '—',
    pledge ? fmtMoney(sponsorPledgeBalance(row)) : '—',
    fmtMoney(row.total_revenue),
    pledge ? fmtPct(row.pct_pledge_paid) : '—',
  ];
}

function fileSlug(title) {
  return (
    String(title || 'sponsors')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 72) || 'sponsors'
  );
}

function autoColWidths(rows) {
  return HEADERS.map((header, idx) => {
    const values = rows.map((row) => String(row[idx] ?? ''));
    const maxLen = values.reduce((max, v) => Math.max(max, v.length), header.length);
    return { wch: Math.min(Math.max(maxLen + 2, 12), 44) };
  });
}

export function exportKotilingaTreeRevenueSponsorsExcel({ title, rows = [] }) {
  const bodyRows = rows.map(mapSponsorRow);
  const generated = new Date();
  const sheet = XLSX.utils.aoa_to_sheet([
    [decodeHtml(title) || 'Sponsors'],
    [`Generated: ${generated.toLocaleString()}`],
    [`Sponsors: ${rows.length}`],
    ['* General donation — non-pledge only (no pledge on this row)'],
    [],
    HEADERS,
    ...bodyRows,
  ]);
  sheet['!cols'] = autoColWidths(bodyRows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Sponsors');
  XLSX.writeFile(book, `${fileSlug(title)}-${generated.toISOString().slice(0, 10)}.xlsx`);
}

export function exportKotilingaTreeRevenueSponsorsPdf({ title, rows = [] }) {
  const bodyRows = rows.map(mapSponsorRow);
  const generated = new Date();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const margin = 36;
  let y = 32;

  doc.setFontSize(13);
  doc.text(decodeHtml(title) || 'Sponsors', margin, y);
  y += 16;
  doc.setFontSize(9);
  doc.text(`Generated ${generated.toLocaleString()} · ${rows.length.toLocaleString()} sponsors (sponsor-level totals)`, margin, y);
  y += 12;
  doc.setFont(undefined, 'italic');
  doc.text('* General donation — non-pledge only (no pledge on this row)', margin, y);
  y += 14;
  doc.setFont(undefined, 'normal');

  autoTable(doc, {
    startY: y,
    head: [HEADERS],
    body: bodyRows,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [0, 105, 120] },
    margin: { left: margin, right: margin },
  });

  doc.save(`${fileSlug(title)}-${generated.toISOString().slice(0, 10)}.pdf`);
}
